import { observer } from 'mobx-react-lite'
import React, { useContext, useState, useEffect, useMemo } from 'react'
import { CampaignContext } from '../../../../../contexts/CampaignContext'
import Switch from '../../../../FormComponents/Switch'
import axiosService from '../../../../../utils/AxiosService'
import Authentication from '../../../../../models/Authentication'
import AudienceInformation from '../../../../../models/AudienceInformation'
import AgeDistribution from './AudienceMetricsParts/AgeDistribution'
import Countries from './AudienceMetricsParts/Countries'
import Gender from './AudienceMetricsParts/Gender'
import Cities from './AudienceMetricsParts/Cities'

export default observer(function AudienceMetrics({ values = {}, setFieldValue = () => {}, advSideScenario, campaignReport }) {
  const { selectedScenario } = useContext(CampaignContext)
  const [authentications, setAuthentications] = useState([])
  const [loading, setLoading] = useState(false)

  const getAudienceInformation = async (authentication) => {
    try {
      const response = await axiosService.get(`/api/project_manager/authentications/${authentication.id}/audience_information`)
      authentication.audience_information = new AudienceInformation(response) || null
      return new Authentication(authentication)
    } catch (error) {
      console.error("Error fetching audience information:", error)
      authentication.audience_information = null
      return new Authentication(authentication)
    }
  }

  const getAuthentications = async (scenario) => {
    setLoading(true)
    try {
      const authIds = [...new Set(scenario?.actions
        .filter(action => action.action_type !== 'non_media_item' && action.visible_in_budget)
        .map(action => action.authentication_id))]

      if (authIds.length === 0) {
        setLoading(false)
        return
      }

      const authPromises = authIds.map(id =>
        axiosService.get(`/api/project_manager/authentications/${id}`)
          .then(auth => getAudienceInformation(auth))
          .catch(err => {
            console.error(`Error fetching authentication ${id}:`, err)
            return null
          })
      )

      const results = await Promise.all(authPromises)
      setAuthentications(results.filter(Boolean))
    } catch (error) {
      console.error("Error in getAuthentications:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (advSideScenario || selectedScenario) {
      getAuthentications(advSideScenario || selectedScenario)
    }
  }, [selectedScenario, advSideScenario])

  const malePercentage = authentications.reduce((acc, auth) => acc + auth?.audience_information?.detailed_audience_stats?.gender?.male, 0) / authentications.length
  const femalePercentage = authentications.reduce((acc, auth) => acc + auth?.audience_information?.detailed_audience_stats?.gender?.female, 0) / authentications.length

  return (
    <div className="bg-white dark:bg-rb-dark-mode border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-2xl p-4 mt-4">
      <div className="flex justify-between items-center mb-8">
        <p className="font-bold text-xl dark:text-white">Audience Metrics</p>
        {!advSideScenario && <Switch name="audience_metrics_visible" value={values.audience_metrics_visible} onChange={({ target: { checked }}) => setFieldValue('audience_metrics_visible', checked, true)} eyeSwitch />}
      </div>
      {
        (advSideScenario || values?.audience_metrics_visible) && (
          <div className="flex flex-col gap-4">
            {(!advSideScenario || (advSideScenario && campaignReport.audience_gender_visible)) && <Gender malePercentage={malePercentage} femalePercentage={femalePercentage} values={values} setFieldValue={setFieldValue} advSideScenario={advSideScenario} />}
            {(!advSideScenario || (advSideScenario && campaignReport.audience_age_visible)) && <AgeDistribution authentications={authentications} values={values} setFieldValue={setFieldValue} advSideScenario={advSideScenario} />}
            {(!advSideScenario || (advSideScenario && campaignReport.audience_countries_visible)) && <Countries authentications={authentications} values={values} setFieldValue={setFieldValue} advSideScenario={advSideScenario} />}
            {(!advSideScenario || (advSideScenario && campaignReport.audience_cities_visible)) && <Cities authentications={authentications} values={values} setFieldValue={setFieldValue} advSideScenario={advSideScenario} />}
          </div>
        )
      }
    </div>
  )
})